import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(CustomEase);

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function initPreloader() {
  const preloader = document.getElementById('site-preloader');
  const html = document.documentElement;
  const minDuration = 700;
  const startedAt = Date.now();

  if (!preloader) {
    html.classList.remove('is-preloading');
    window.grahamPreloader = { isComplete: true };
    document.dispatchEvent(new CustomEvent('graham:preloader:done'));
    return;
  }

  // Detect variant from data attribute (circle | logo).
  const preloaderType = preloader.getAttribute('data-preloader-type') || 'circle';

  const state = {
    isComplete: false,
    loadDone: false,
    fontsDone: false,
    introDone: preloaderType !== 'logo',
    heroStarted: false,
  };

  const reducedMotion = prefersReducedMotion();
  let loopTl;
  let iterationCount = 0;

  // ─── Circle variant ────────────────────────────────────────────────────────

  if (preloaderType === 'circle') {
    const spinner = preloader.querySelector('.site-preloader__spinner');
    const hubCircle = preloader.querySelector('.site-preloader__hub-circle');
    const hubRingCircle = preloader.querySelector('.site-preloader__hub-ring-circle');

    //
    // Radius math in px (hub circle r, hubRing circle r):
    //
    // | Keyframe | hub px | hub r | border px | ring outer px | ring r |
    // |----------|--------|-------|-----------|---------------|--------|
    // | Start    |  80    |  40   |   5       |   90          |  45    |
    // | KF 1     | 100    |  50   |  10.8     |  121.6        |  60.8  |
    // | KF 2     | 128    |  64   |  22       |  172          |  86    |
    // | KF 3     | 120    |  60   |  12       |  144          |  72    |
    // | KF 4     |  80    |  40   |   5       |   90          |  45    |
    //
    // Growth animates the SVG circle's "r" attribute directly rather than a CSS
    // transform: scale. Scaling via transform stretches an already-rasterized
    // compositor texture (blurry/pixelated at larger sizes); animating "r" forces
    // a real repaint of the vector path at its true size every frame, so the
    // circle stays crisp — matching the vector redraw seen in the Figma preview.
    //
    // borderColor opacity: 0.5 at rest/KF1/KF4 → 1.0 at KF2/KF3
    // animated as fill on the hubRing's inner <circle>
    //

    if (!reducedMotion) {
      CustomEase.create('preloaderFigmaSpring', 'M0,0 C0.05,0.6 0.1,1 1,1');
      CustomEase.create('preloaderFigmaSpringNew', 'M0,0 C0.1,0.55 0.15,1 1,1');

      gsap.set(spinner, { rotation: 0, autoAlpha: 0, transformOrigin: '50% 50%' });
      gsap.set(hubCircle, { attr: { r: 40 } });
      gsap.set(hubRingCircle, { attr: { r: 45 }, fill: 'rgba(152, 183, 189, 0.5)' });

      loopTl = gsap.timeline({
        repeat: -1,
        delay: 0.6,
        repeatDelay: 0.1,
        onRepeat: function () {
          iterationCount++;
          maybeComplete();
        },
      });

      // KF 1: hub r 40→50, ring r 45→60.8
      loopTl.to(hubCircle, { attr: { r: 50 }, duration: 0.22, ease: 'power2.out' }, 0);
      loopTl.to(hubRingCircle, { attr: { r: 60.8 }, fill: 'rgba(152, 183, 189, 0.5)', duration: 0.22, ease: 'power2.out' }, 0);
      loopTl.to(spinner, { rotation: -45, autoAlpha: 0.5, duration: 0.22, ease: 'power2.out' }, 0);

      // KF 2: hub r 50→64, ring r 60.8→86
      loopTl.to(hubCircle, { attr: { r: 64 }, duration: 0.22, ease: 'preloaderFigmaSpringNew' }, '+=0.1');
      loopTl.to(hubRingCircle, { attr: { r: 86 }, fill: 'rgba(152, 183, 189, 1)', duration: 0.22, ease: 'preloaderFigmaSpringNew' }, '<');
      loopTl.to(spinner, { rotation: 0, autoAlpha: 1, duration: 0.22, ease: 'preloaderFigmaSpringNew' }, '<');

      // KF 3: hub r 64→60, ring r 86→72
      loopTl.to(hubCircle, { attr: { r: 60 }, duration: 0.22, ease: 'preloaderFigmaSpring' }, '+=0.1');
      loopTl.to(hubRingCircle, { attr: { r: 72 }, fill: 'rgba(152, 183, 189, 1)', duration: 0.22, ease: 'preloaderFigmaSpring' }, '<');
      loopTl.to(spinner, { rotation: 45, autoAlpha: 0.2, duration: 0.22, ease: 'preloaderFigmaSpring' }, '<');

      // KF 4: hub r 60→40, ring r 72→45
      loopTl.to(hubCircle, { attr: { r: 40 }, duration: 0.6, ease: 'power2.out' }, '+=0.1');
      loopTl.to(hubRingCircle, { attr: { r: 45 }, fill: 'rgba(152, 183, 189, 0.5)', duration: 0.6, ease: 'power2.out' }, '<');
      loopTl.to(spinner, { rotation: 0, autoAlpha: 0, duration: 0.6, ease: 'power2.out' }, '<');
    }
  }

  // ─── Logo variant ──────────────────────────────────────────────────────────

  if (preloaderType === 'logo') {
    const fillRect = document.getElementById('site-preloader-g-fill-rect');
    const wavesEl = preloader.querySelector('.site-preloader__waves');
    const logoTextEl = preloader.querySelector('.site-preloader__logo-text');
    const gWrapEl = preloader.querySelector('.site-preloader__g-wrap');
    const gSvgEl = preloader.querySelector('.site-preloader__g-svg');

    // viewBox height is 96; 10% filled means rect exposes 9.6px from bottom
    // so fillRect.y starts at 96 - 9.6 = 86.4 ≈ 86
    const FILL_START_Y = 86;

    if (!reducedMotion) {
      // ── Initial states ──────────────────────────────────────────────────
      gsap.set(wavesEl, { autoAlpha: 0 });
      gsap.set(logoTextEl, { autoAlpha: 0 });
      gsap.set(gWrapEl, { autoAlpha: 0 });
      if (fillRect) {
        gsap.set(fillRect, { attr: { y: FILL_START_Y } });
      }

      const introTl = gsap.timeline({
        delay: 0.1,
        onComplete: function () {
          state.introDone = true;
          maybeComplete();
        },
      });

      // ── Step 1: G appears already ~10% filled, fade in in-place (1000ms) ──
      introTl.to(gWrapEl, {
        autoAlpha: 1,
        duration: 1,
        ease: 'linear',
      }, 0);

      // ── Step 2: waves appear at opacity 0.2; G fills fully (1000ms) ────
      introTl.to(wavesEl, {
        opacity: 0.2,
        visibility: 'visible',
        duration: 1.2,
        ease: 'linear',
      }, '+=0');

      if (fillRect) {
        introTl.to(fillRect, {
          attr: { y: 0 },
          duration: 1,
          ease: 'power2.inOut',
        }, '<');
      }

      // ── Step 3: waves jump to opacity 1; G shifts from-right; text appears ──
      introTl.to(wavesEl, {
        opacity: 1,
        duration: 1.2,
        ease: 'linear',
      }, '-=0.4');

      introTl.from(gSvgEl, {
        x: 160,
        duration: 1,
        ease: 'power2.out',
        clearProps: 'x',
      }, '<');

      introTl.to(logoTextEl, {
        autoAlpha: 1,
        duration: 0.6,
        ease: 'linear',
      }, '<');
    } else {
      // Reduced motion: show everything immediately
      gsap.set([gWrapEl, wavesEl, logoTextEl], { autoAlpha: 1 });
      if (fillRect) {
        gsap.set(fillRect, { attr: { y: 0 } });
      }
      state.introDone = true;
    }
  }

  // ─── Shared: finish / hide / maybeComplete ─────────────────────────────────

  function finish() {
    if (state.isComplete) {
      return;
    }

    state.isComplete = true;
    html.classList.remove('is-preloading');
    preloader.setAttribute('aria-hidden', 'true');
    preloader.setAttribute('aria-busy', 'false');
    preloader.classList.add('is-hidden');

    window.setTimeout(function () {
      if (preloader.parentNode) {
        preloader.parentNode.removeChild(preloader);
      }
    }, 400);
  }

  function hidePreloader() {
    const elapsed = Date.now() - startedAt;
    const waitTime = Math.max(0, minDuration - elapsed);

    function runOutro() {
      // Trigger dependent modules before the outro animation so they overlap.
      if (!state.heroStarted) {
        state.heroStarted = true;
        window.grahamPreloader = { isComplete: true };
        document.dispatchEvent(new CustomEvent('graham:preloader:done'));
      }

      if (reducedMotion) {
        finish();
        return;
      }

      if (loopTl) {
        loopTl.pause();
      }

      const tl = gsap.timeline({ onComplete: finish });

      if (preloaderType === 'circle') {
        const spinner = preloader.querySelector('.site-preloader__spinner');
        const hub = preloader.querySelector('.site-preloader__hub');
        const hubRing = preloader.querySelector('.site-preloader__hub-ring');

        tl.to(spinner, {
          autoAlpha: 0,
          duration: 0.6,
          ease: 'power3.inOut',
          transformOrigin: '50% 50%',
        }, 0);

        tl.to(hub, {
          autoAlpha: 0,
          duration: 0.6,
          ease: 'power2.out',
        }, 0);

        tl.to(hubRing, {
          autoAlpha: 0,
          duration: 0.6,
          ease: 'power2.out',
        }, 0);

        // Circle variant: fade the whole overlay out.
        tl.to(preloader, {
          autoAlpha: 0,
          duration: 0.6,
          ease: 'power2.inOut',
        }, 0);
      }

      if (preloaderType === 'logo') {
        // Logo variant: slide the overlay up off-screen (600ms).
        tl.to(preloader, {
          yPercent: -100,
          duration: 0.6,
          ease: 'power2.inOut',
        }, 0);
      }
    }

    if (waitTime > 0) {
      window.setTimeout(runOutro, waitTime);
    } else {
      runOutro();
    }
  }

  function maybeComplete() {
    if (!state.loadDone || !state.fontsDone || !state.introDone || state.isComplete) {
      return;
    }

    // For the circle variant wait at least one full animation loop before hiding.
    if (preloaderType === 'circle' && !reducedMotion && iterationCount < 1) {
      return;
    }

    hidePreloader();
  }

  window.addEventListener('load', function () {
    state.loadDone = true;
    maybeComplete();
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      state.fontsDone = true;
      maybeComplete();
    });
  } else {
    state.fontsDone = true;
  }

  window.grahamPreloader = { isComplete: false };
}
